/**
 * The part that does the actual showing.
 *
 * Everything else in this folder exists so that this can happen: the screen goes
 * dark except for one button, which is lit, ringed and pulsing, with one short
 * sentence beside it. Somebody who has read "press the plus in the bottom bar"
 * three times without finding it finds it here in under a second, because there
 * is nothing else on the screen to look at.
 *
 * Three things make it work on a real app rather than a demo:
 *
 * **It waits.** "Show me how to add a photo" means pressing the + and then
 * pointing at a choice inside a sheet that does not exist yet. Each step
 * navigates if it needs to, then watches the document until its button appears.
 *
 * **It gets into the top layer.** The Add sheet is a `<dialog>` opened with
 * `showModal()`, which paints above every z-index there is. So the overlay is
 * portalled *into* the topmost open dialog when there is one, and into the body
 * when there is not. Without this the spotlight would be underneath exactly the
 * sheets it most needs to point inside.
 *
 * **It does not trap anybody.** The overlay ignores the mouse entirely except
 * for its own caption, so the lit button is the real button and pressing it does
 * the real thing — which also advances the walk. Nobody is held hostage to the
 * end of a tutorial; wandering off is allowed and costs nothing.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../components/Icon';
import { useI18n } from '../i18n';
import { useRouter } from '../app/router';
import { useApp } from '../app/AppState';
import { findAnchor, revealAnchor, waitForAnchor } from './anchors';
import { controlById } from './knowledge/appMap';
import { useAssistant } from './AssistantState';

interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** A little room around the button, so the ring does not sit on its edge. */
const PAD = 8;

/**
 * Where to draw, so that the overlay is actually on top.
 *
 * A modal `<dialog>` lives in the browser's top layer, above every z-index there
 * is, so an overlay in the body simply cannot cover it — and the sheets are
 * exactly what a walkthrough most needs to point inside. So the overlay is
 * portalled into a dialog when one is open: the one holding the target if that
 * is where it is, otherwise the most recently opened, which is the one on top.
 */
function topLayerHost(target: HTMLElement | null): HTMLElement {
  const own = target?.closest?.('dialog[open]');
  if (own instanceof HTMLElement) return own;
  const dialogs = [...document.querySelectorAll<HTMLDialogElement>('dialog[open]')];
  return dialogs.length ? dialogs[dialogs.length - 1] : document.body;
}

export function Spotlight() {
  const { t, lang } = useI18n();
  const { walk, nextStep, endWalk, speak, speaksAloud } = useAssistant();
  const { path, navigate } = useRouter();
  const { reducedMotionActive } = useApp();

  const [box, setBox] = useState<Box | null>(null);
  const [host, setHost] = useState<HTMLElement | null>(null);
  const [lost, setLost] = useState(false);
  /*
   * The button being pointed at, as state rather than a ref.
   *
   * It is found asynchronously — the step may navigate first, and the sheet it
   * lives in may still be opening — so a ref would be null on the render that
   * wants to attach the "they pressed it" listener, and the walk would never
   * advance by itself. Found the hard way, in a real browser.
   */
  const [target, setTarget] = useState<HTMLElement | null>(null);
  const targetRef = useRef<HTMLElement | null>(null);
  targetRef.current = target;

  const step = walk ? walk.steps[walk.index] : null;
  const total = walk?.steps.length ?? 0;
  const index = walk?.index ?? 0;

  // ---- find this step's button, navigating first if it asked us to ----
  useEffect(() => {
    if (!step) {
      setBox(null);
      setLost(false);
      setTarget(null);
      return;
    }
    let cancelled = false;
    setLost(false);
    setBox(null);

    const go = async () => {
      if (step.route && step.route !== path) navigate(step.route);
      if (!step.anchor) {
        // A step with words but nothing to point at: show the caption alone.
        if (!cancelled) setHost(topLayerHost(null));
        return;
      }
      const el = await waitForAnchor(step.anchor);
      if (cancelled) return;
      if (!el) {
        setLost(true);
        setTarget(null);
        setHost(topLayerHost(null));
        return;
      }
      setTarget(el);
      revealAnchor(el, !reducedMotionActive);
      setHost(topLayerHost(el));
      // After a scroll, measure on the next frame rather than mid-flight.
      requestAnimationFrame(() => {
        if (!cancelled) measure();
      });
    };
    void go();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, walk?.index]);

  const measure = useCallback(() => {
    const el = targetRef.current;
    if (!el || !el.isConnected) return;
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return;
    const next = { top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 };
    // Measured every frame (see below), but only *told* to React when the button has actually moved: a fresh
    // object each frame re-rendered the dimmed screen sixty times a second for as long as somebody took to press.
    setBox((was) => (was && was.top === next.top && was.left === next.left && was.width === next.width && was.height === next.height ? was : next));
  }, []);

  /*
   * Keep the ring on the button while the page moves underneath it — a sheet
   * sliding up, a list scrolling, the keyboard opening on a phone. A frame loop
   * rather than scroll listeners, because the movement that matters most is a
   * CSS animation, which fires no events at all.
   */
  useEffect(() => {
    if (!target || lost) return;
    let raf = 0;
    const tick = () => {
      measure();
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    const onResize = () => measure();
    window.addEventListener('resize', onResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
    };
  }, [target, lost, measure]);

  /*
   * Each step is read out as it lights up.
   *
   * This is what makes the walkthrough work without anybody reading: press the
   * glowing button, hear what to do next, press the next glowing button. Someone
   * holding the phone at arm's length never has to find the caption at all — and
   * the caption is written short enough to be heard rather than scanned.
   */
  useEffect(() => {
    if (!step || !speaksAloud) return;
    speak(step.say);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, walk?.index, speaksAloud]);

  // Pressing the real button is the same as pressing Next — that is the point.
  useEffect(() => {
    if (!target) return;
    const onClick = () => {
      // Let the app's own handler run and the next sheet settle first.
      setTimeout(() => nextStep(), 260);
    };
    target.addEventListener('click', onClick);
    return () => target.removeEventListener('click', onClick);
  }, [target, nextStep]);

  // Escape gets out, like every other overlay in the app.
  useEffect(() => {
    if (!walk) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        endWalk();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [walk, endWalk]);

  if (!walk || !step) return null;
  const label = step.anchor ? controlById(step.anchor)?.name[lang] : null;
  const where = step.anchor ? controlById(step.anchor)?.where[lang] : null;
  const last = index + 1 >= total;
  /*
   * Is there a real button under the ring right now?
   *
   * All three have to be true: the step names one, it was found, and it is on
   * the screen with a listener on it. If any is not, nobody can press their way
   * forward and the caption has to offer a way instead.
   */
  const mustPress = !!step.anchor && !lost && !!target;

  const overlay = (
    <div className="assist-spot" role="dialog" aria-live="polite" aria-label={t('assistant.showing')}>
      {box ? (
        <svg className="assist-spot-mask" aria-hidden="true">
          <defs>
            <mask id="assist-hole">
              <rect x="0" y="0" width="100%" height="100%" fill="#fff" />
              <rect x={box.left} y={box.top} width={box.width} height={box.height} rx={Math.min(24, box.height / 2)} fill="#000" />
            </mask>
          </defs>
          <rect x="0" y="0" width="100%" height="100%" mask="url(#assist-hole)" className="assist-spot-dim" />
        </svg>
      ) : (
        <div className="assist-spot-plain" aria-hidden="true" />
      )}

      {box && <div className="assist-spot-ring" style={{ top: box.top, left: box.left, width: box.width, height: box.height, borderRadius: Math.min(24, box.height / 2) }} aria-hidden="true" />}

      <div className={`assist-caption ${box ? '' : 'is-centred'}`} style={box ? captionPosition(box) : undefined}>
        {total > 1 && (
          <span className="assist-caption-count" aria-hidden="true">
            {index + 1}/{total}
          </span>
        )}
        <p className="assist-caption-say">{step.say}</p>
        {lost && (
          <p className="assist-caption-lost">
            {label && where ? t('assistant.lookFor', { what: label, where }) : t('assistant.cantShow')}
          </p>
        )}
        {/*
          * When there is a real button lit up, pressing it is the only way on.
          *
          * There used to be a Next here in every case, and it did not advance the
          * walk so much as abandon it: Jay pressed Next on "open the menu"
          * without opening the menu, and the walk cheerfully moved to "now press
          * Settings" — pointing at a Settings button that was not on the screen,
          * inside a menu that was never opened. A step that says *press this* and
          * also offers a way past it is teaching people that the lit button is
          * optional, which is the opposite of what a walkthrough is for.
          *
          * So: a step with a button to press has one way on — press it — and one
          * way out, which stops the whole thing. A step with nothing to point at
          * (words alone, or a button this screen turned out not to have) still
          * needs a way forward, because otherwise it is a dead end; that is the
          * only case where Next survives.
          */}
        {mustPress ? (
          <div className="assist-caption-actions is-waiting">
            <span className="assist-caption-press">
              <Icon name="tap" />
              {t('assistant.pressLit')}
            </span>
            <button type="button" className="btn btn-sm btn-ghost" onClick={endWalk}>
              {t('assistant.stopWalk')}
            </button>
          </div>
        ) : (
          <div className="assist-caption-actions">
            <button type="button" className="btn btn-sm btn-ghost" onClick={endWalk}>
              {t('nav.close')}
            </button>
            <button type="button" className="btn btn-sm btn-primary" onClick={last ? endWalk : nextStep} data-autofocus-skip>
              <span>{last ? t('assistant.gotIt') : t('nav.next')}</span>
              {!last && <Icon name="forward" className="icon-mirror" />}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // A sheet that closes is removed from the document, taking any portal with
  // it — so a host that is no longer connected falls back to the body.
  return createPortal(overlay, host?.isConnected ? host : document.body);
}

/**
 * Put the caption where it will not cover the thing it is describing, and will
 * not fall off the screen. Below the button by default, above it when the button
 * is low — which is most of the time, because the app's own Add button lives at
 * the bottom of every screen.
 */
function captionPosition(box: Box): React.CSSProperties {
  const belowRoom = window.innerHeight - (box.top + box.height);
  const below = belowRoom > 190;
  const width = Math.min(320, window.innerWidth - 32);
  let left = box.left + box.width / 2 - width / 2;
  left = Math.max(16, Math.min(left, window.innerWidth - width - 16));
  return below
    ? { top: box.top + box.height + 14, left, width }
    : { bottom: window.innerHeight - box.top + 14, left, width };
}

/** Whether an anchor is on screen right now — used by the panel to keep quiet about what it cannot show. */
export function anchorVisible(id: string): boolean {
  return !!findAnchor(id);
}
